import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BinaryWriter } from "@bufbuild/protobuf/wire";

export interface ProtobufHttpOptions {
  headers?: { [key: string]: string };
  params?: { [key: string]: string };
  responseType?: 'protobuf' | 'json' | 'text';
}

@Injectable({
  providedIn: 'root'
})
export class ProtobufService {
  private readonly defaultHeaders = {
    'Content-Type': 'application/x-protobuf',
    'Accept': 'application/x-protobuf'
  };

  constructor(private http: HttpClient) {}

  /**
   * POST method with protobuf encoding/decoding
   * @param url - The endpoint URL
   * @param message - The protobuf message instance to send
   */
  post(
    url: string,
    message: BinaryWriter,
  ): Observable<Uint8Array> {
    return this.sendRequest(
      'POST',
      url,
      message,
    );
  }

  // POST method to send protobuf and receive json
  postJSON<T>(
    url: string,
    message: BinaryWriter,
  ): Observable<T> {
    const buffer = message.finish().buffer;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-protobuf',
      'Accept': 'application/json'
    });
    return this.http.post<T>(url, buffer, { headers });
  }

  /**
   * PUT method with protobuf encoding/decoding
   */
  put(
    url: string,
    message: BinaryWriter,
  ): Observable<Uint8Array> {
    return this.sendRequest(
      'PUT',
      url,
      message,
    );
  }

  /**
   * Generic GET method with protobuf decoding
   */
  get(
    url: string,
  ): Observable<Uint8Array> {
    return this.sendRequest(
      'GET',
      url
    );
  }

  /**
   * Generic DELETE method
   */
  delete(url: string, options: ProtobufHttpOptions = {}): Observable<void> {
    return this.http.delete<void>(url, {
      headers: this.defaultHeaders,
      params: options.params
    }).pipe(
      catchError(this.handleError)
    );
  }
  /**
   * Private method to handle the actual HTTP request
   */
  private sendRequest(
    method: string,
    url: string,
    message?: BinaryWriter,
  ): Observable<Uint8Array> {


    let buffer = undefined;
    if (message) {
      const encoded = message.finish();
      buffer = encoded.buffer;
    }

    const headers = new HttpHeaders({
      ...this.defaultHeaders
    });

    const httpOptions = {
      headers:headers,
      responseType: 'arraybuffer' as const
    };

    let httpCall: Observable<ArrayBuffer>;
    
    switch (method.toUpperCase()) {
      case 'GET':
        httpCall = this.http.get(url, httpOptions);
        break;
      case 'POST':
        httpCall = this.http.post(url, buffer, httpOptions);
        break;
      case 'PUT':
        httpCall = this.http.put(url, buffer, httpOptions);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return httpCall.pipe(
      map((buffer: ArrayBuffer) => {
        return new Uint8Array(buffer);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Error handler
   */
  private handleError = (error: any): Observable<never> => {
    console.error('ProtobufService error:', error);
    
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server error: ${error.status} - ${error.message}`;
    }
    
    return throwError(() => new Error(errorMessage));
  };
}
